import { Views } from 'react-big-calendar';
import { format as dateFnsFormat } from 'date-fns';

/**
 * Supported views for the Calendar component.
 */
export const CALENDAR_VIEWS = [
  Views.MONTH,
  Views.WEEK,
  Views.DAY,
  Views.AGENDA
];

/**
 * Default number of days to show in the Agenda view.
 */
export const DEFAULT_AGENDA_LENGTH = 29;

/**
 * Custom date formats for different parts of the React Big Calendar.
 */
export const CALENDAR_FORMATS = {
  // Week / Day header: "31 Tue"
  dayFormat: (date: Date) => dateFnsFormat(date, 'd EEE'),
  
  // Month view day-of-week row: "Sun" "Mon"
  weekdayFormat: (date: Date) => dateFnsFormat(date, 'EEE'),
  
  // Month view date cells: "1", "2"
  dateFormat: (date: Date) => dateFnsFormat(date, 'd'),
  
  // Time gutter labels: "9:00 AM"
  timeGutterFormat: (date: Date) => dateFnsFormat(date, 'h:mm a'),
  
  // Day view header: "Tuesday, March 31"
  dayHeaderFormat: (date: Date) => dateFnsFormat(date, 'EEEE, MMMM d'),
  
  // Week view range header: "Mar 29 – Apr 4, 2026"
  dayRangeHeaderFormat: ({ start, end }: { start: Date; end: Date }) =>
    `${dateFnsFormat(start, 'MMM d')} – ${dateFnsFormat(end, 'MMM d, yyyy')}`,
    
  // Agenda view date column
  agendaDateFormat: (date: Date) => dateFnsFormat(date, 'EEE MMM d'),
  
  // Agenda view time column (start only)
  agendaTimeFormat: (date: Date) => dateFnsFormat(date, 'h:mm a'),
  
  // Agenda view time column (range)
  agendaTimeRangeFormat: ({ start, end }: { start: Date; end: Date }) =>
    `${dateFnsFormat(start, 'h:mm a')} – ${dateFnsFormat(end, 'h:mm a')}`,
    
  // Agenda view table header range
  // RBC range is inclusive: length=N shows [start, start+N]. The end passed here IS the last visible day.
  agendaHeaderFormat: ({ start, end }: { start: Date; end: Date }) => {
    if (dateFnsFormat(start, 'yyyy-MM-dd') === dateFnsFormat(end, 'yyyy-MM-dd')) {
      return dateFnsFormat(start, 'MMM d, yyyy');
    }
    return `${dateFnsFormat(start, 'MMM d')} – ${dateFnsFormat(end, 'MMM d, yyyy')}`;
  },
    
  // Event time display in week/day cells
  eventTimeRangeFormat: ({ start, end }: { start: Date; end: Date }) =>
    `${dateFnsFormat(start, 'h:mm a')} – ${dateFnsFormat(end, 'h:mm a')}`,
};

/**
 * Shared palette used for ALL non-bookable cells in the calendar grid.
 * Used across the Day/Week slot columns.
 */
export const NON_BOOKABLE_CELL_COLORS = {
  GREY: '#F1F1F1',
  RED: 'rgba(255, 0, 0, 0.08)'
};